import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, X, Send, Plus, Trash2, MessageSquare, Paperclip, FileText, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { AiProposalCards } from "@/components/AiProposalCards";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";

function renderWithLinks(text) {
  const parts = String(text).split(/((?:https?:\/\/[^\s)]+)|(?:\/api\/chat-exports\/[^\s)]+))/g);
  return parts.map((p, i) => {
    if (/^(https?:\/\/|\/api\/chat-exports\/)/.test(p)) {
      const href = p.startsWith("/") ? `${BACKEND_BASE}${p}` : p;
      return (
        <a key={i} href={href} target="_blank" rel="noreferrer"
           className="underline font-medium text-emerald-700 hover:text-emerald-900 break-all"
           data-testid="assistant-download-link">
          📥 {p.includes("chat-exports") ? "Dokument herunterladen" : p}
        </a>
      );
    }
    return p;
  });
}


const SUGGESTIONS = [
  "Wie viele offene Kundenrechnungen habe ich?",
  "Was war mein Umsatz dieses Jahr?",
  "Top 3 Aufwände diesen Monat?",
  "Wann sind die nächsten Eingangsrechnungen fällig?",
  "Wie steht es um meinen letzten Lohnlauf?",
];

export default function AssistantWidget() {
  const { company } = useCompany();
  const companyId = company?._id;
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [proposalTick, setProposalTick] = useState(0);
  const [modelLabel, setModelLabel] = useState("KI-Assistent");
  const [fallback, setFallback] = useState(null);
  const [budget, setBudget] = useState(null);
  const [activity, setActivity] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEnd = useRef(null);

  const handleFiles = async (files) => {
    if (!files?.length || !companyId) return;
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, 5)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await apiClient.post(`/companies/${companyId}/assistant/uploads`, fd);
        setAttachments((prev) => [...prev, { id: r.data.upload_id, filename: r.data.filename }]);
        if (r.data.warning) toast.warning(`${r.data.filename}: ${r.data.warning}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!sending || !activeSession?.id || !companyId) {
      setActivity(null);
      return;
    }
    const iv = setInterval(() => {
      apiClient
        .get(`/companies/${companyId}/assistant/sessions/${activeSession.id}/activity`)
        .then((r) => setActivity(r.data?.activity || null))
        .catch(() => {});
    }, 700);
    return () => clearInterval(iv);
  }, [sending, activeSession?.id, companyId]);

  useEffect(() => {
    if (!open || !companyId) return;
    apiClient.get(`/companies/${companyId}/assistant/model`)
      .then((r) => { setModelLabel(r.data?.display || "KI-Assistent"); setFallback(r.data?.fallback_active ? r.data : null); setBudget(r.data?.budget?.warning ? r.data.budget : null); })
      .catch(() => {});
  }, [open, companyId]);

  const scrollToBottom = () => {
    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  const loadSessions = useCallback(async () => {
    if (!companyId) return;
    setLoadingSessions(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/assistant/sessions`);
      setSessions(r.data || []);
    } catch (e) {
      // Optional silent
    } finally {
      setLoadingSessions(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (open && companyId) loadSessions();
  }, [open, companyId, loadSessions]);

  const newSession = async () => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/assistant/sessions`, {});
      setActiveSession(r.data);
      setMessages([]);
      loadSessions();
    } catch (e) {
      toast.error("Session konnte nicht erstellt werden");
    }
  };

  const openSession = async (s) => {
    setActiveSession(s);
    try {
      const r = await apiClient.get(`/companies/${companyId}/assistant/sessions/${s.id}`);
      setMessages(r.data.messages || []);
      scrollToBottom();
    } catch (e) {
      toast.error("Konnte Verlauf nicht laden");
    }
  };

  const deleteSession = async (s) => {
    if (!window.confirm(`Session "${s.title}" wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/assistant/sessions/${s.id}`);
      toast.success("Session gelöscht");
      if (activeSession?.id === s.id) {
        setActiveSession(null);
        setMessages([]);
      }
      loadSessions();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const send = async (text) => {
    const userText = (text || draft).trim();
    if (!userText && attachments.length === 0) return;
    const finalText = userText || "Bitte analysiere die angehängte(n) Datei(en).";
    let sid = activeSession?.id;
    if (!sid) {
      // Auto-create
      try {
        const r = await apiClient.post(`/companies/${companyId}/assistant/sessions`, {});
        sid = r.data.id;
        setActiveSession(r.data);
      } catch (e) {
        toast.error("Session konnte nicht erstellt werden");
        return;
      }
    }
    const sendAttachments = attachments;
    setAttachments([]);
    setDraft("");
    setSending(true);
    // Optimistic UI
    const optimistic = {
      role: "user", text: finalText,
      attachments: sendAttachments.map((a) => a.filename),
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    scrollToBottom();
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/assistant/sessions/${sid}/messages`,
        { text: finalText, attachments: sendAttachments.map((a) => a.id) },
      );
      setMessages((prev) => [
        ...prev.filter((m) => m !== optimistic),
        r.data.user_message,
        r.data.assistant_message,
      ]);
      setBudget(r.data.budget?.warning ? r.data.budget : null);
      setProposalTick((t) => t + 1);
      scrollToBottom();
      loadSessions();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m !== optimistic));
      if (e.response?.status === 402) {
        setBudget({ warning: "blocked", blocked: true, message: e.response.data?.detail });
        toast.error(e.response.data?.detail || "KI-Budget aufgebraucht", { action: { label: "Credits kaufen", onClick: () => { window.location.href = "/settings#section-ai-usage"; } } });
      } else {
        toast.error(e.response?.data?.detail || "Antwort vom KI-Service ist fehlgeschlagen");
      }
    } finally {
      setSending(false);
    }
  };

  // Cmd+I → toggle assistant
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "i") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (!companyId) return null;

  return (
    <>
      {/* Floating Trigger Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center group"
          data-testid="assistant-fab"
          title="KI-Assistent öffnen (Ctrl+I)"
        >
          <Sparkles className="h-6 w-6 group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -right-1 bg-amber-400 text-amber-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full">KI</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 pointer-events-none" data-testid="assistant-drawer">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-[640px] bg-white shadow-2xl pointer-events-auto flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-violet-50 to-fuchsia-50">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">Frag deine Buchhaltung</h2>
                  <p className="text-[10px] text-slate-500" data-testid="assistant-model-label">
                    {fallback ? "Claude (Ausweichweg)" : modelLabel} · {company?.name}
                    {fallback && <span className="ml-1 text-amber-700" title={fallback.fallback_reason || ""} data-testid="assistant-fallback-badge"> · OpenAI nicht verfügbar</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setShowSidebar(!showSidebar)} data-testid="assistant-sessions-toggle">
                  <MessageSquare className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={newSession} data-testid="assistant-new-session">
                  <Plus className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="assistant-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {budget?.warning && (
              <div className={`flex items-start gap-2 px-4 py-2 text-xs border-b ${budget.blocked ? "bg-rose-50 text-rose-800" : "bg-amber-50 text-amber-800"}`} data-testid="assistant-budget-banner">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="flex-1">{budget.message}</span>
                <a href="/settings#section-ai-usage" className="underline whitespace-nowrap" data-testid="assistant-budget-link">{budget.blocked ? "Credits kaufen" : "Details"}</a>
              </div>
            )}

            <div className="flex-1 flex overflow-hidden">
              {/* Sessions sidebar */}
              {showSidebar && (
                <div className="w-48 border-r overflow-y-auto bg-slate-50">
                  <div className="p-2 space-y-1">
                    {loadingSessions ? (
                      <div className="text-xs text-slate-400 p-2">Lade …</div>
                    ) : sessions.length === 0 ? (
                      <div className="text-xs text-slate-400 p-2">Keine Sessions</div>
                    ) : (
                      sessions.map((s) => (
                        <div
                          key={s.id}
                          className={`group flex items-center gap-1 text-xs rounded px-2 py-1.5 cursor-pointer ${
                            activeSession?.id === s.id ? "bg-white border border-violet-200" : "hover:bg-white"
                          }`}
                          onClick={() => openSession(s)}
                          data-testid={`assistant-session-${s.id}`}
                        >
                          <span className="flex-1 truncate text-slate-700">{s.title}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteSession(s); }}
                            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
                  {messages.length === 0 ? (
                    <div className="py-6">
                      <div className="text-center mb-6">
                        <Sparkles className="h-12 w-12 mx-auto text-violet-300 mb-2" />
                        <p className="text-sm text-slate-600 font-medium">Frag mich etwas über deine Buchhaltung</p>
                        <p className="text-xs text-slate-400 mt-1">Ich kenne deine aktuellen Zahlen, offenen Posten und letzten Belege.</p>
                      </div>
                      <div className="grid gap-2">
                        {SUGGESTIONS.map((q, i) => (
                          <button
                            key={i}
                            onClick={() => send(q)}
                            disabled={sending}
                            className="text-left text-sm px-3 py-2 rounded-lg border border-violet-200 bg-violet-50/50 hover:bg-violet-100 transition-colors"
                            data-testid={`assistant-suggestion-${i}`}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    messages.map((m, i) => (
                      <div
                        key={i}
                        className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                        data-testid={`assistant-message-${m.role}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                            m.role === "user"
                              ? "bg-violet-600 text-white"
                              : "bg-slate-100 text-slate-800 border border-slate-200"
                          }`}
                        >
                          {m.attachments?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1.5">
                              {m.attachments.map((name, ai) => (
                                <span key={ai} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${m.role === "user" ? "bg-violet-500 text-violet-100" : "bg-slate-200 text-slate-600"}`} data-testid="assistant-message-attachment">
                                  <FileText className="h-3 w-3" /> {name}
                                </span>
                              ))}
                            </div>
                          )}
                          {m.text ? renderWithLinks(m.text) : m.text}
                        </div>
                      </div>
                    ))
                  )}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-2xl px-3.5 py-2 text-sm text-slate-500 flex items-center gap-2" data-testid="assistant-typing-indicator">
                        <span className="animate-pulse">●</span>
                        <span className="animate-pulse [animation-delay:0.2s]">●</span>
                        <span className="animate-pulse [animation-delay:0.4s]">●</span>
                        {activity && (
                          <span className="text-xs text-indigo-600 font-medium ml-1" data-testid="assistant-activity-label">
                            {activity}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {activeSession && (
                    <AiProposalCards companyId={companyId} sessionId={activeSession.id} refreshToken={proposalTick}
                      onChanged={() => setProposalTick((t) => t + 1)} />
                  )}
                  <div ref={messagesEnd} />
                </div>

                {/* Input */}
                <div className="border-t p-3 bg-slate-50">
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="assistant-attachment-chips">
                      {attachments.map((a) => (
                        <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-violet-100 text-violet-800 px-2 py-1 rounded-full">
                          <FileText className="h-3 w-3" /> {a.filename}
                          <button type="button" onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} className="hover:text-rose-600" data-testid="assistant-attachment-remove">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="flex gap-2"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.csv,.txt"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                      data-testid="assistant-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending || uploading}
                      title="Bild oder Dokument anhängen"
                      data-testid="assistant-attach-btn"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </Button>
                    <Input
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={attachments.length ? "Frage zur Datei …" : "Frag mich etwas …"}
                      disabled={sending}
                      autoFocus
                      data-testid="assistant-input"
                    />
                    <Button
                      type="submit"
                      disabled={sending || uploading || (!draft.trim() && attachments.length === 0)}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="assistant-send"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                  <div className="text-[10px] text-slate-400 mt-1.5 text-center">
                    Ctrl+I zum Öffnen · Powered by {modelLabel}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
